import React from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Select,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import {
  CADENCE_DAY_CODES,
  MAX_CADENCE_SLOTS_PER_WEEK,
  formatTimeLocal,
  parseTimeLocal,
  type CadenceDayCode,
  type CadenceFieldSlot,
} from '@modules/content-plan/utils/strategyCadence';

export interface PlatformCadenceFieldsProps {
  mode: 'author' | 'strategy';
  slots: CadenceFieldSlot[];
  onSlotsChange: (slots: CadenceFieldSlot[]) => void;
  emptyText?: string;
  testIdPrefix: string;
}

export const PlatformCadenceFields: React.FC<PlatformCadenceFieldsProps> = ({
  mode,
  slots,
  onSlotsChange,
  emptyText = 'No slots configured. Add slots to define weekly volume.',
  testIdPrefix,
}) => {
  const visibleSlots = slots.slice(0, MAX_CADENCE_SLOTS_PER_WEEK);
  const canAddSlot = visibleSlots.length < MAX_CADENCE_SLOTS_PER_WEEK;

  const updateSlot = (idx: number, patch: Partial<CadenceFieldSlot>) => {
    onSlotsChange(visibleSlots.map((slot, i) => (i === idx ? { ...slot, ...patch } : slot)));
  };

  const removeSlot = (idx: number) => {
    onSlotsChange(visibleSlots.filter((_, i) => i !== idx));
  };

  const addSlot = () => {
    if (!canAddSlot) return;
    const previous = visibleSlots[visibleSlots.length - 1];
    const nextDay = CADENCE_DAY_CODES[visibleSlots.length % CADENCE_DAY_CODES.length] ?? 'Mon';
    const nextHour = previous ? (Math.max(0, Math.min(23, previous.hour)) + 1) % 24 : 9;
    onSlotsChange([
      ...visibleSlots,
      { day: nextDay, hour: nextHour, score: mode === 'author' ? 0.7 : undefined },
    ]);
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2">Preferred posting slots</Typography>
            <Chip size="small" variant="outlined" label={`${visibleSlots.length} / week`} />
          </Stack>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={addSlot}
            disabled={!canAddSlot}
            data-testid={`${testIdPrefix}-addSlot`}
          >
            Add slot
          </Button>
        </Stack>
        {visibleSlots.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            {emptyText}
          </Typography>
        )}
        {visibleSlots.length > 0 && (
          <Box
            sx={{
              display: { xs: 'none', sm: 'grid' },
              gridTemplateColumns: '120px 130px minmax(160px, 1fr) 40px',
              gap: 1,
              px: 0.25,
              mb: 0.5,
            }}
          >
            <Typography variant="caption" color="text.secondary">Day</Typography>
            <Typography variant="caption" color="text.secondary">Time</Typography>
            <Typography variant="caption" color="text.secondary">Score</Typography>
            <Box />
          </Box>
        )}
        <Stack spacing={1.25}>
          {visibleSlots.map((slot, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '120px 130px minmax(160px, 1fr) 40px' },
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Select
                size="small"
                value={slot.day}
                onChange={(event) => updateSlot(idx, { day: event.target.value as CadenceDayCode })}
                sx={{ width: 110 }}
                data-testid={`${testIdPrefix}-slot-${idx}-day`}
              >
                {CADENCE_DAY_CODES.map((day) => (
                  <MenuItem key={day} value={day}>{day}</MenuItem>
                ))}
              </Select>
              <TextField
                size="small"
                type="time"
                label="Time"
                value={formatTimeLocal(slot.hour, slot.minute)}
                onChange={(event) => {
                  const parsed = parseTimeLocal(event.target.value);
                  updateSlot(idx, {
                    hour: parsed.hour,
                    ...(parsed.minute ? { minute: parsed.minute } : { minute: undefined }),
                  });
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300, 'data-testid': `${testIdPrefix}-slot-${idx}-hour` }}
                sx={{ width: 130 }}
              />
              <Box sx={{ flex: 1, px: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: { xs: 'block', sm: 'none' } }}
                >
                  Score
                </Typography>
                <Slider
                  value={slot.score ?? 0.7}
                  onChange={(_, value) => updateSlot(idx, { score: value as number })}
                  min={0}
                  max={1}
                  step={0.05}
                  valueLabelDisplay="auto"
                  size="small"
                  data-testid={`${testIdPrefix}-slot-${idx}-score`}
                />
              </Box>
              <IconButton
                size="small"
                onClick={() => removeSlot(idx)}
                data-testid={`${testIdPrefix}-slot-${idx}-remove`}
                aria-label="Remove slot"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
};

export default PlatformCadenceFields;
